import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Spinner } from "reactstrap";
import useApiCallService from "../../Common/Helpers/ApiCall";
import { REPORT_PROCEEDING_PATHS } from "../../config";
import { SlidePanel } from "../../Common/Components/Modal/SlidePanel";
import { CustomInput } from "../../Common/Components/Input/CustomInput";
import { ChangeDateFormat } from "../../Common/Helpers/ChangeDateFormat";
import $ from "jquery";
import RequestReimbursementV2 from "../Models/Request/RequestReimbursementV2";
import { InputMessage } from "../../AppSystem/Helpers/InputMessage";

/**
 * SelectOrgPanel
 *
 * Opens as a SlidePanel before navigating to the reimbursement creation form.
 * Fetches the user's organisations from /reimbursement/v2/info and lets the
 * user pick one, then navigates to the creation form with ?orgId=... preset.
 *
 * Props:
 *   isOpen  boolean
 *   onClose () => void
 */
export function SelectOrgPanel({ isOpen, onClose }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { ApiCall } = useApiCallService();

    const [orgs, setOrgs] = useState([]);
    const [selectedOrgId, setSelectedOrgId] = useState("");
    const [loading, setLoading] = useState(false);
    const [infoResponse, setInfoResponse] = useState(null);

    // Paso 2 — formulario
    const [step, setStep] = useState(1);
    const [date, setDate] = useState("");
    const [toDate, setToDate] = useState(null);
    const [purpose, setPurpose] = useState("");
    const [costBearers, setCostBearers] = useState([]);
    const [selectedCostBearerId, setSelectedCostBearerId] = useState("");

    // Reset al cerrar/abrir
    useEffect(() => {
        if (!isOpen) {
            setStep(1);
            setDate("");
            setToDate(null);
            setPurpose("");
            setSelectedCostBearerId("");
            return;
        }

        setLoading(true);
        setSelectedOrgId("");

        ApiCall({ method: "get", url: "/reimbursement/v2/info" }, (res) => {
            setLoading(false);
            setInfoResponse(res);
            if (!res?.orgsByUser) return;
            setOrgs(
                res.orgsByUser.map((o) => ({
                    id: o.id,
                    label: o.type ? `${o.name} (${o.type})` : o.name,
                    type: o.type,
                }))
            );
            // pre-select first org
            if (res.orgsByUser.length > 0) {
                setSelectedOrgId(res.orgsByUser[0].id);
            }
        });
    }, [isOpen]);

    // Recalcular cost bearers cuando cambia la org
    useEffect(() => {
        if (!selectedOrgId || !infoResponse) return;
        const org = infoResponse.orgsByUser?.find(o => o.id === selectedOrgId);
        if (!org) return;
        const byType = infoResponse.costBearersByOrgType?.find(x => x.type === org.type);
        if (byType && infoResponse.costBearers) {
            const list = infoResponse.costBearers
                .filter(cb => byType.costBearerNames.includes(cb.name))
                .map(cb => ({
                    id: cb.id,
                    name: cb.showLabel || cb.name,
                    message: cb.message,
                }));
            setCostBearers(list);
            setSelectedCostBearerId(list.length === 1 ? list[0].id : "");
        } else {
            setCostBearers([]);
            setSelectedCostBearerId("");
        }
    }, [selectedOrgId, infoResponse]);

    const handleConfirmStep1 = () => {
        if (!selectedOrgId) return;
        setStep(2);
    };

    const checkRequired = () => {
        if (!date || date === "") return false;
        if (!purpose || purpose === "") return false;
        if (costBearers.length > 0 && !selectedCostBearerId) return false;
        return true;
    };

    const handleConfirmStep2 = () => {
        if (!checkRequired()) return;
        onClose();
        navigate(REPORT_PROCEEDING_PATHS.REPORT_PROCEEDING, {
            state: {
                orgId: selectedOrgId,
                date,
                toDate: (toDate !== null && toDate !== "") ? toDate : null,
                purpose,
                costBearerId: selectedCostBearerId || null,
            }
        });
    };

    return (
        <SlidePanel
            isOpen={isOpen}
            onClose={onClose}
            closeIcon={step === 1 ? "x" : "arrow"}
            onBack={step === 2 ? () => setStep(1) : onClose}
            title={
                step === 1
                    ? (t("reimbursement.newProcessing") || "Skapa ny ersättningsbegäran")
                    : (t("reimbursement.reportProcessing") || "Ersättningsbegäran")
            }
            variant="center"
        >
        {/* ── Paso 1: select organization ── */}
        {step === 1 && (
            <div className="select-org-panel-wrapper">
                <p className="select-org-panel-intro">
                    {t("reimbursement.createAs", { type: t("reimbursement.reportProcessing") }) || "Skapa Ersättningsbegäran som:"}
                </p>

                {loading ? (
                    <div className="select-org-panel-loading">
                        <Spinner color="success" size="sm" />
                        <span>{t("sortableList.gettingData")}</span>
                    </div>
                ) : (
                    <>
                        <div className="select-org-panel-selector-wrap">
                            <label htmlFor="selectOrgId" className="select-org-panel-label">
                                {t("reimbursement.selectOrg") || "Organisation och roll"}
                            </label>
                            <div className="select-org-panel-select-wrap">
                                <select
                                    id="selectOrgId"
                                    value={selectedOrgId}
                                    onChange={(e) => setSelectedOrgId(e.target.value)}
                                    className="select-org-panel-select"
                                >
                                    {orgs.map((o) => (
                                        <option key={o.id} value={o.id}>{o.label}</option>
                                    ))}
                                </select>
                                <span className="select-org-panel-chevron">▾</span>
                            </div>
                        </div>

                        <div className="select-org-panel-buttons">
                            <button onClick={onClose} className="select-org-panel-btn-cancel">
                                {t("button.cancel") || "Avbryt"}
                            </button>
                            <button
                                onClick={handleConfirmStep1}
                                disabled={!selectedOrgId}
                                className={`select-org-panel-btn-confirm ${selectedOrgId ? "enabled" : "disabled"}`}
                            >
                                {t("button.continue") || "Fortsätt"}
                            </button>
                        </div>
                    </>
                )}
            </div>
            )}

            {/* ── Paso 2: basic data of the reimbursement ── */}
            {step === 2 && (
                <div className="select-org-panel-wrapper">

                    {/* Fecha */}
                    <div className="mb-3" onClick={() => $('.react-datepicker__tab-loop').removeClass('d-none')}>
                        <label className="form-label rpv2-date-label">
                            {toDate !== null ? t('modelLabel.fromDateReimbursement') : t('modelLabel.dateReimbursement')}
                        </label>
                        <CustomInput
                            model={RequestReimbursementV2}
                            attribute="date"
                            data={date}
                            customChange={(e) => setDate(e && e !== "" ? ChangeDateFormat('date', e) : "")}
                        />
                        {toDate !== null && (
                            <CustomInput
                                model={RequestReimbursementV2}
                                attribute="toDate"
                                data={toDate}
                                minDate={date}
                                customChange={(e) => setToDate(e && e !== "" ? ChangeDateFormat('date', e) : "")}
                            />
                        )}
                        <div className="rpv2-multiday-row">
                            <input
                                type="checkbox"
                                className="rpv2-multiday-checkbox"
                                checked={toDate !== null}
                                onChange={() => setToDate(prev => prev === null ? "" : null)}
                            />
                            <label className="rpv2-multiday-label">
                                {t('modelLabel.multipleDayEvent')}
                            </label>
                        </div>
                    </div>

                    {/* Syfte */}
                    <div className="mb-3">
                        <CustomInput
                            model={RequestReimbursementV2}
                            attribute="purpose"
                            data={purpose}
                            customChange={(e) => setPurpose(e?.target?.value || "")}
                        />
                    </div>

                    {/* Cost bearer */}
                    {costBearers.length > 0 && (
                        <div className="mb-3">
                            <label className="select-org-panel-label">
                                {t('reimbursement.costBearer') || "Aktivitetsområde"}
                            </label>
                            <div className="select-org-panel-select-wrap mb-2">
                                <select
                                    value={selectedCostBearerId}
                                    onChange={(e) => setSelectedCostBearerId(e.target.value)}
                                    className="select-org-panel-select"
                                >
                                    {costBearers.length > 1 && (
                                        <option value="" disabled={true}>
                                            {t('formPlaceholder.selectOption') || "Välj ett alternativ"}
                                        </option>
                                    )}
                                    {costBearers.map(cb => (
                                        <option key={cb.id} value={cb.id}>{cb.name}</option>
                                    ))}
                                </select>
                                <span className="select-org-panel-chevron">▾</span>
                            </div>
                            {InputMessage(costBearers, selectedCostBearerId)}
                        </div>
                    )}

                    {/* Botones */}
                    <div className="select-org-panel-buttons">
                        <button onClick={() => setStep(1)} className="select-org-panel-btn-cancel">
                            {t("button.back") || "Tillbaka"}
                        </button>
                        <button
                            onClick={handleConfirmStep2}
                            disabled={!checkRequired()}
                            className={`select-org-panel-btn-confirm ${checkRequired() ? "enabled" : "disabled"}`}
                        >
                            {t("button.continue") || "Fortsätt"}
                        </button>
                    </div>
                </div>
            )}
        </SlidePanel>
    );
}